import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FeatureFlagService, LanguageService, NavigationService, ProviderSettingsService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { breakPoints, Language } from '@core/utils';
import { AuthService } from '@core/auth';
import { NavigationEnd, Router } from '@angular/router';
import { ProviderLinkOptions } from '@shared/models';
import { environment } from '@environment';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  navigationOpen = false;
  windowResizing = false;
  showAccessCardTab!: boolean;
  enableBilling!: boolean;
  enableVoucher!: boolean;

  providerLinks!: ProviderLinkOptions;

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private navigationService: NavigationService,
    private authService: AuthService,
    private featureFlagService: FeatureFlagService,
    private providerSettingsService: ProviderSettingsService,
  ) {}

  ngOnInit(): void {
    this.showAccessCardTab = this.featureFlagService.getFeatureFlag('door_opener_qr_code');
    const externalBillingEnabled = this.featureFlagService.getFeatureFlag('enable_external_billing');
    this.enableBilling = !externalBillingEnabled;
    this.enableVoucher = this.featureFlagService.getFeatureFlag('enable_voucher');


    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });

    this.navigationService.navigationOpenStateSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.navigationOpen = value;
    });

    // Disable navigation bar transition on window resize to prevent unnecessary animation
    window.addEventListener('resize', () => {
      this.windowResizing = true;

      if (window.innerWidth >= breakPoints.md) {
        this.closeNavigation();
      }
    });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeNavigation();
      }
    });
  }

  getCurrentLanguage(): string {
    return this.languageService.getCurrentLanguage();
  }

  isInternalLink(url: string): boolean {
    return url.startsWith('/');
  }

  changeLanguage(language: Language): void {
    this.languageService.setLanguage(language);
  }

  closeNavigation(): void {
    this.windowResizing = false;
    this.navigationService.navigationOpenStateSubject.next(false);
  }

  onLogOutClicked(): void {
    this.closeNavigation();
    this.authService.logOut();
  }

  protected readonly environment = environment;
}
